/**
 * Uplink ver10 module
 * @author Andrew D.Laptev <a.d.laptev@gmail.com>
 * @see https://www.onvif.org/ver10/uplink/wsdl/uplink.wsdl
 */

import { Onvif, OnvifServices } from './onvif';
import { StringList } from './interfaces/onvif';
import Service from './service';
import {
  Capabilities,
  Configuration,
  DeleteUplink,
  GetUplinksResponse,
  SetUplink,
} from './interfaces/uplink';

/**
 * Uplink service
 */
export class Uplink extends Service {
  constructor(onvif: Onvif, service: keyof OnvifServices) {
    super(onvif, service);
  }

  private static stringListToBuild(items: StringList) {
    return items.length === 1 ? items[0] : items;
  }

  private static configurationToBuild(configuration: Configuration) {
    return {
      RemoteAddress: configuration.remoteAddress,
      ...(configuration.certificateID && { CertificateID: configuration.certificateID }),
      UserLevel: Uplink.stringListToBuild(configuration.userLevel),
      ...(configuration.certPathValidationPolicyID && {
        CertPathValidationPolicyID: configuration.certPathValidationPolicyID,
      }),
      ...(configuration.authorizationServer && {
        AuthorizationServer: configuration.authorizationServer,
      }),
    };
  }

  /**
   * Returns the capabilities of the uplink service.
   */
  async getServiceCapabilities(): Promise<Capabilities> {
    const response = await this.request({
      GetServiceCapabilities: {},
    });
    return response.getServiceCapabilitiesResponse?.capabilities ?? {};
  }

  /**
   * Returns configured uplink connections.
   */
  async getUplinks(): Promise<GetUplinksResponse['configuration']> {
    const response = await this.request(
      {
        GetUplinks: {},
      },
      { array: ['configuration'] },
    );
    return response.getUplinksResponse?.configuration ?? [];
  }

  /**
   * Adds or modifies an uplink configuration.
   * @param options
   */
  async setUplink({ configuration }: SetUplink): Promise<void> {
    await this.request({
      SetUplink: {
        Configuration: Uplink.configurationToBuild(configuration),
      },
    });
  }

  /**
   * Deletes an uplink configuration.
   * @param options
   */
  async deleteUplink({ remoteAddress }: DeleteUplink): Promise<void> {
    await this.request({
      DeleteUplink: {
        RemoteAddress: remoteAddress,
      },
    });
  }
}
